import React from 'react';

import type CK from '~/types/contentking';

import useDataLoading from '~/hooks/useDataLoading';
import useFetchFurtherRange from '~/hooks/useFetchFurtherRange';



type Edges<TNode> = ReadonlyArray<{
	cursor: string,
	loadedAt: number,
	node: TNode,
} | null>;



function useGraphQLConnection<TQueryData extends Record<string, any>, TNode>(
	input: {
		batchLimit: number,
		context?: {
			columns?: any,
			filter?: any,
			sortBy?: any,
			websiteId?: CK.WebsiteId | null,
		},
		customIsNotLoaded?: (edges: Edges<TNode>) => boolean,
		data: TQueryData | undefined,
		fallbackTotalCount?: number,
		fetchMore: (input: {
			variables: {
				limit: number,
				offset: number,
			},
		}) => Promise<{
			data: TQueryData,
		}>,
		getter: (data: TQueryData) => ({
			edges: Edges<TNode>,
			totalCount: number,
		} | null),
		shouldPoll?: boolean,
		pollIntervalInMilliseconds: number,
	},
) {
	const {
		batchLimit,
		context,
		customIsNotLoaded = null,
		data,
		fallbackTotalCount,
		fetchMore,
		getter,
		shouldPoll = true,
		pollIntervalInMilliseconds,
	} = input;

	const connection = data !== undefined
		? getter(data)
		: null;

	const edges = connection?.edges ?? null;
	const totalCount = connection?.totalCount ?? fallbackTotalCount ?? 0;

	// Use ref to avoid capturing entire edges array (350k+ items) in closure
	// This prevents memory leaks per Jake Archibald's closure GC article
	// CRITICAL: Update ref synchronously during render, not in useEffect
	// This ensures rowGetter reads fresh data when edges change
	const edgesRef = React.useRef(edges);
	const previousEdgesRef = React.useRef<typeof edges>(null);
	const edgesVersionRef = React.useRef(0);

	// Track edges length to trigger re-renders when data changes
	// This ensures components re-render when edgesRef is updated
	const edgesLength = edges?.length ?? 0;

	// Update ref synchronously during render to avoid race condition
	// Track version to detect edges array reference changes (even if length unchanged)
	// This ensures rowGetter identity changes when data content updates via polling
	if (edges !== previousEdgesRef.current) {
		edgesRef.current = edges;
		previousEdgesRef.current = edges;
		edgesVersionRef.current += 1;
	}

	const edgesVersion = edgesVersionRef.current;

	const rowGetter = React.useCallback(
		({ rowIndex }: { rowIndex: number }) => {
			return edgesRef.current?.[rowIndex]?.node ?? null;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[edgesLength, edgesVersion], // Intentionally included to change rowGetter identity when edges change (for memoized components)
	);

	const fetchFurtherRange = useFetchFurtherRange(
		connection,
		batchLimit,
		pollIntervalInMilliseconds,
		fetchMore,
		customIsNotLoaded,
	);

	return {
		fetchFurtherRange: useDataLoading(fetchFurtherRange, Object.values(context ?? {}), shouldPoll),
		isLoaded: connection !== null,
		rowGetter,
		totalCount,
	};
}



export default useGraphQLConnection;
